import { useMemo } from "react";
import classnames from "classnames";
import { useTable, useSortBy, Column } from "react-table";
import { useResponsive } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getTierColor } from "@ugg/shared/utils/tier-helpers";
import { numberWithCommas, round } from "@ugg/shared/utils/math";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import ChampionCounter from "@ugg/shared/components/common/Tooltips/ChampionCounter";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { MatchupsInfo } from "@ugg/shared/api/data-parser/champions/matchups";

function getCounterHeader(dataFor: string) {
  return (
    <TooltipContainer tooltipInfo={`Matchups's ${dataFor} Difference at 15 minutes`}>
      <span>{dataFor} @15</span>
    </TooltipContainer>
  );
}

const CountersWinRate = (props: { championId: number; data: MatchupsInfo["counters"][number] }) => {
  const { championId, data } = props;

  const params = {
    curChampionId: data.champion_id,
    secChampionId: championId,
    datum: {
      ...data,
      win_rate: 100 - data.win_rate,
      sec_win_rate: data.win_rate,
      wins: round((100 - data.win_rate) * data.matches),
    },
  };

  return (
    <ChampionCounter data={params}>
      <div className={getTierColor(params.datum.sec_win_rate)}>
        <b>{params.datum.sec_win_rate.toFixed(2) + "%"}</b>
      </div>
    </ChampionCounter>
  );
};

const CounterChampionName = (props: {
  championId: number;
  oppChampionId: number;
  data: MatchupsInfo["counters"][number];
  role: string;
}) => {
  const { currentBreakpoint } = useResponsive();
  const isMobileSmall = ["MOBILE_SMALL", "MOBILE_MEDIUM"].includes(currentBreakpoint);
  const { championId, oppChampionId, data, role } = props;
  const params = {
    curChampionId: oppChampionId,
    secChampionId: championId,
    datum: {
      ...data,
      win_rate: 100 - data.win_rate,
      sec_win_rate: data.win_rate,
      wins: round((100 - data.win_rate) * data.matches),
    },
  };

  return (
    <ChampionCounter data={params}>
      <div className="flex flex-1 overflow-hidden">
        <ChampionLink championId={Number(oppChampionId)} role={role} dim={isMobileSmall ? 24 : 30} />
      </div>
    </ChampionCounter>
  );
};

interface MatchupsTableInterface {
  data: MatchupsInfo["counters"];
  championId: number;
  role: string;
}

export const MatchupsTable = (props: MatchupsTableInterface) => {
  const { getChampionName } = getRiotAssetsContext();

  const { data, championId, role } = props;

  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  const columns = useMemo<Column<MatchupsInfo["counters"][number]>[]>(
    () => [
      {
        id: "rank",
        accessor: "champion_id",
        Header: <span>Rank</span>,
        responsiveClassName: "min-w-[50px] max-w-[50px]",
        headerClassName: "rank max-xs:invisible",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: <span>Champion</span>,
        accessor: "champion_id",
        className: "champion xs:!py-[12px]",
        headerClassName: "champion xxxs:max-xs:[&>*]:hidden",
        responsiveClassName: "min-w-[135px]",
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_id);
          const champNameB = getChampionName(b.values.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: (props) => (
          <CounterChampionName oppChampionId={props.value} data={props.cell.row.original} championId={championId} role={role} />
        ),
      },
      {
        id: "winrate",
        Header: "Win Rate",
        accessor: "win_rate",
        headerClassName: "win_rate",
        className: "win_rate",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        Cell: (props) => <CountersWinRate championId={championId} data={props.cell.row.original} />,
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.winrate - b.values.winrate;
        },
      },
      {
        Header: getCounterHeader("± Gold"),
        accessor: "gold_adv_15",
        headerClassName: "gold",
        className: "gold",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.gold_adv_15 - b.values.gold_adv_15;
        },
      },
      {
        Header: getCounterHeader("± XP"),
        accessor: "xp_adv_15",
        headerClassName: "xp",
        className: "xp",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.xp_adv_15 - b.values.xp_adv_15;
        },
      },
      {
        Header: getCounterHeader("± Kills"),
        accessor: "kill_adv_15",
        headerClassName: "kill",
        className: "kill",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.kill_adv_15 - b.values.kill_adv_15;
        },
      },
      {
        Header: getCounterHeader("± CS"),
        accessor: "cs_adv_15",
        headerClassName: "cs",
        className: "cs",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.cs_adv_15 - b.values.cs_adv_15;
        },
      },
      {
        Header: getCounterHeader("± Jungle CS"),
        accessor: "jungle_cs_adv_15",
        headerClassName: "jungle_cs",
        className: "jungle_cs",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.jungle_cs_adv_15 - b.values.jungle_cs_adv_15;
        },
      },
      {
        Header: "Matches",
        accessor: "matches",
        headerClassName: "matches",
        className: "matches",
        responsiveClassName: "min-w-[70px] max-w-[110px]",
        sortDescFirst: true,
        Cell: ({ cell: { value } }) => <span>{numberWithCommas(value)}</span>,
      },
    ],
    [role],
  );

  const columnStyle = () => ({
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: "winrate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  if (tableData.length === 0) {
    return <TierListTableError />;
  }

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
        <div
          {...getTableProps({
            className: "content-section ReactTable ugg-table-2 matchups-table min-w-[900px]",
          })}
        >
          <div className={"rt-thead -header"}>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
                {headerGroup.headers.map((column) => {
                  const thClassNames = classnames([
                    "rt-th",
                    column.headerClassName,
                    column.responsiveClassName,
                    column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                    column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                  ]);

                  return (
                    <div
                      className={thClassNames}
                      {...column.getHeaderProps(
                        column.getSortByToggleProps({
                          style: columnStyle(),
                        }),
                      )}
                    >
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, i) => {
              prepareRow(row);

              return (
                <div key={i} className="rt-tr-group">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, index) => {
                      const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      });

                      return (
                        <div
                          {...cell.getCellProps({
                            className: tdClassNames,
                            style: columnStyle(),
                          })}
                        >
                          {cell.render("Cell", { index: i })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
